import React, { useState, useEffect, useMemo, useRef } from "react";
import { MenuItem, Icon, Box, Text } from "@showitinc/showit-wc";
import { useHotkeys } from "react-hotkeys-hook";
import { useClickAway } from "react-use";
import Fuse from "fuse.js";

import UIActions from "../../actions/UIActions";
import { FORMS_FLAG } from "../../constants/FeatureFlags.mjs";
import useV2Feature from "../../hooks/useV2Feature";
import CommandPaletteStore, {
  canAddFormFields,
  formFieldCommands,
} from "../../stores/CommandPaletteStore";
import useStoreState from "../../components/providers/useStoreState";
import styles from "./CommandPalette.module.css";

const SEARCH_OPTIONS = { includeScore: true, threshold: 0.4, keys: ["name", "tags"] };

const CommandPalette = () => {
  const commandPalette = useStoreState(
    CommandPaletteStore,
    CommandPaletteStore.getCommandPaletteState
  );
  const [filter, setFilter] = useState("");
  const [filteredCommands, setFilteredCommands] = useState([]);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);
  const commandPaletteRef = useRef();
  const commandInputRef = useRef();

  const isFormFieldsEnabled = useV2Feature(FORMS_FLAG);
  // `commandPalette` changes on every open, so the site canvas check sees the current selection.
  const commandSearchIndex = useMemo(
    () =>
      new Fuse(
        isFormFieldsEnabled && canAddFormFields()
          ? [...commandPalette.commands, ...formFieldCommands]
          : commandPalette.commands,
        SEARCH_OPTIONS
      ),
    [commandPalette, isFormFieldsEnabled]
  );

  useClickAway(commandPaletteRef, () => {
    UIActions.hideCommandPalette();
  });

  useEffect(() => {
    setFilteredCommands(commandSearchIndex?.search(filter));
  }, [filter, commandSearchIndex]);

  useEffect(() => {
    if (!commandPalette.visible) setFilter("");
    if (commandPalette.visible && commandInputRef.current) {
      commandInputRef.current.focus();
    }
  }, [commandPalette]);

  useHotkeys("mod+p", () => UIActions.showCommandPalette(), {
    preventDefault: true,
    enableOnFormTags: true,
  });
  useHotkeys("esc", () => UIActions.hideCommandPalette(), {
    preventDefault: true,
    enableOnFormTags: true,
  });
  useHotkeys("mod+shift+d", () => UIActions.toggleColorMode(), {
    preventDefault: true,
    enableOnFormTags: true,
  });
  useHotkeys("mod+shift+\\", () => UIActions.toggleDensity(), {
    preventDefault: true,
    enableOnFormTags: true,
  });

  const onFilterInput = (event) => {
    setFilter(event.target.value);
    setSelectedOptionIndex(0);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      UIActions.hideCommandPalette();
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedOptionIndex((prevIndex) =>
        prevIndex > 0 ? prevIndex - 1 : filteredCommands.length - 1
      );
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedOptionIndex((prevIndex) =>
        prevIndex < filteredCommands.length - 1 ? prevIndex + 1 : 0
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      _onCommand(filteredCommands[selectedOptionIndex].item.action);
      setFilter("");
    }
  };

  const _onCommand = (action) => {
    action();
    UIActions.hideCommandPalette();
  };

  if (!commandPalette.visible) {
    return null;
  }

  return (
    <div id="command-palette-overlay" className={styles.commandPaletteOverlay}>
      <div ref={commandPaletteRef} id="command-palette" className={styles.commandPalette}>
        <input
          className={styles.commandPaletteInput}
          type="text"
          value={filter}
          onKeyDown={handleKeyDown}
          onChange={onFilterInput}
          placeholder="Search pages, canvases, settings, or commands..."
          ref={commandInputRef}
        />
        {filteredCommands.slice(0, 20).map(({ item }, index) => (
          <MenuItem
            onClick={() => _onCommand(item.action)}
            key={index}
            selected={selectedOptionIndex === index}
            className={selectedOptionIndex === index ? "selected" : ""}
          >
            <Box d="flex" align="center" className={styles.commandPaletteItem}>
              <Icon size="20" className={styles.commandIcon} type={item.icon || "settings"} />
              <Text
                className={styles.commandPaletteItemText}
                dangerouslySetInnerHTML={{ __html: item.name }}
              />
            </Box>
          </MenuItem>
        ))}
      </div>
    </div>
  );
};

export default CommandPalette;
